/**
 * The "Élő mező hozzáadása" palette — the operator-facing face of the
 * conventional live keys: serviceToDesign seeds them, applyLiveData refreshes
 * them at mint, applyEventPatchToDesign updates them on flight events. Adding
 * a palette field = subscribing that spot on the pass to live data. Keys and
 * changeMessage MUST stay in sync with wallet-core (see its tests).
 *
 * Also hosts `conventionalLiveKeys(style)` — the STATIC per-style (group, key)
 * map that decides canvas liveness. Liveness must NOT derive from the loaded
 * doc (the old seed-based rule): after save + reload the GET-one doc IS the
 * seed, so a saved CUSTOM field's key would join the live set and its value
 * would lock in the UI forever.
 */
import type { AppleStyle, PassDesign, PassDesignField } from '@insurwiz/wallet-core';

import { FIELD_GROUPS, type FieldGroup } from '../../lib/fieldEditing';

export interface LiveFieldSpec {
  key: string;
  /** Where the field lands when added. */
  group: FieldGroup;
  /** Operator-facing name + why it's live. */
  title: string;
  description: string;
  field: PassDesignField;
  /**
   * Styles this entry is live/offerable on. The WHOLE palette belongs to the
   * travel pass (flight events write gate/status/terminal, boarding reminders
   * write notice, the seed + mint refresh write departure and the passenger),
   * and the templates-only demo binds the travel template exclusively to the
   * boardingPass canvas — so every entry is boardingPass-only. Off
   * boardingPass (the generic/assistance canvas has no flight at all) an
   * offered entry would be a permanently locked '—' promising updates that
   * never come.
   */
  styles?: readonly AppleStyle[];
}

// Labels + banners match the travel seed (lib/designSeed travelizeSeed) — an
// added field reads exactly like the seeded one it replaces.
export const LIVE_FIELD_PALETTE: readonly LiveFieldSpec[] = [
  {
    key: 'gate',
    group: 'auxiliary',
    title: 'Kapu',
    description: 'Járatesemények frissítik.',
    field: { key: 'gate', label: 'KAPU', value: '—', changeMessage: 'Kapu: %@' },
    styles: ['boardingPass'],
  },
  {
    key: 'status',
    group: 'auxiliary',
    title: 'Státusz / késés',
    description: 'Időben → Késik (+n perc) automatikusan.',
    field: { key: 'status', label: 'STÁTUSZ', value: 'Időben', changeMessage: '%@' },
    styles: ['boardingPass'],
  },
  {
    key: 'departure',
    group: 'auxiliary',
    title: 'Indulási idő',
    description: 'Menetrendi indulás, az indulási repülőtér helyi idejével.',
    field: { key: 'departure', label: 'INDULÁS', value: '—', changeMessage: '%@' },
    styles: ['boardingPass'],
  },
  {
    key: 'terminal',
    group: 'auxiliary',
    title: 'Terminál',
    description: 'Járatesemények frissítik.',
    field: { key: 'terminal', label: 'TERMINÁL', value: '—', changeMessage: '%@' },
    styles: ['boardingPass'],
  },
  {
    key: 'notice',
    group: 'back',
    title: 'Értesítés (hátlap)',
    description: 'Beszállás-emlékeztetők ide érkeznek.',
    field: { key: 'notice', label: 'Értesítés', value: '—', changeMessage: '%@' },
    styles: ['boardingPass'],
  },
  {
    key: 'passenger',
    group: 'secondary',
    title: 'Utas neve',
    description: 'Generáláskor a címzett neve kerül ide.',
    field: { key: 'passenger', label: 'UTAS', value: 'Minta Utas' },
    styles: ['boardingPass'],
  },
] as const;

export function liveFieldsAvailable(design: PassDesign): LiveFieldSpec[] {
  const present = new Set<string>();
  for (const rows of Object.values(design.fields)) {
    for (const f of rows ?? []) present.add(f.key);
  }
  return LIVE_FIELD_PALETTE.filter(
    (s) => !present.has(s.key) && (s.styles === undefined || s.styles.includes(design.style)),
  );
}

// Per-style seeded (group → keys) contract, mirroring the ACTUAL seeds
// (lib/designSeed composeSeed over wallet-core serviceToDesign). boardingPass
// IS the travel canvas: flight/origin/destination, the passenger in
// secondary (where the lounge render vehicle sat) + the aux trio, nothing on
// the back. Every other style is the general (assistance) card: title +
// lounge, with the full lounge service pool (lounge, lounge_code,
// access_window — serviceToContent's lounge fields, relabelled) on the back.
const BOARDING_PASS_SEEDED: Readonly<Partial<Record<FieldGroup, readonly string[]>>> = {
  header: ['flight'],
  primary: ['origin', 'destination'],
  secondary: ['passenger'],
  auxiliary: ['departure', 'gate', 'status'],
};
const OTHER_STYLE_SEEDED: Readonly<Partial<Record<FieldGroup, readonly string[]>>> = {
  primary: ['title'],
  secondary: ['lounge'],
  back: ['lounge', 'lounge_code', 'access_window'],
};

function buildConventionalLiveKeys(style: AppleStyle): Record<FieldGroup, ReadonlySet<string>> {
  const seeded = style === 'boardingPass' ? BOARDING_PASS_SEEDED : OTHER_STYLE_SEEDED;
  const map = {} as Record<FieldGroup, Set<string>>;
  for (const group of FIELD_GROUPS) {
    map[group] = new Set(seeded[group] ?? []);
  }
  // The palette keys are conventional on every style they are OFFERED on
  // (spec.styles gate — omitted ⇒ all styles; today the whole palette is
  // boardingPass-only): adding one is the documented "subscribe this spot to
  // live data" act, and applyEventPatchToDesign updates those keys by key —
  // so a palette-added gate/terminal/notice must stay live after save +
  // reload on the styles it is offered on. Off boardingPass only the SEEDED
  // service keys (title/lounge pool — mint-refreshed) stay live.
  for (const spec of LIVE_FIELD_PALETTE) {
    if (spec.styles === undefined || spec.styles.includes(style)) {
      map[spec.group].add(spec.key);
    }
  }
  return map;
}

const CONVENTIONAL_LIVE_KEYS = new Map<AppleStyle, Record<FieldGroup, ReadonlySet<string>>>();

/**
 * The canvas liveness rule: a field is LIVE iff its key is in this map's set
 * for its (style, group). Static — a saved custom field can never become
 * live, no matter what the loaded doc contains.
 */
export function conventionalLiveKeys(style: AppleStyle): Record<FieldGroup, ReadonlySet<string>> {
  let map = CONVENTIONAL_LIVE_KEYS.get(style);
  if (map === undefined) {
    map = buildConventionalLiveKeys(style);
    CONVENTIONAL_LIVE_KEYS.set(style, map);
  }
  return map;
}

const CONVENTIONAL_LIVE_KEY_UNION = new Map<AppleStyle, ReadonlySet<string>>();

/**
 * Union of ALL groups' conventional live keys for a style — the reserved pool
 * for custom-field slug dedupe. applyEventPatchToDesign patches by key across
 * every group, so a custom field labelled "Gate" in ANY group must dedupe
 * away from the bare 'gate' key or it would get engine-patched while the
 * canvas shows it as a fixed, editable field.
 */
export function conventionalLiveKeyUnion(style: AppleStyle): ReadonlySet<string> {
  let set = CONVENTIONAL_LIVE_KEY_UNION.get(style);
  if (set === undefined) {
    const map = conventionalLiveKeys(style);
    set = new Set(FIELD_GROUPS.flatMap((group) => [...map[group]]));
    CONVENTIONAL_LIVE_KEY_UNION.set(style, set);
  }
  return set;
}
